import { Box, Button } from '@mui/material'
import { Loader } from '~/shared/ui'
import {
  carePlan as carePlanApi,
  CarePlanCreateResponse,
  CarePlanRetrieveResponse,
  CarePlanV2ListResponse,
} from '@fireflyhealth/api'
import { anyIncompleteTask } from './utils'
import { CarePlanModalTask } from './useCarePlanModal'
import { setDueDateToEOD } from '~/shared/lib/date'
import type { PatientUser } from '~/shared/types'
import type { RootState } from '~/app/store/configureStore'
import { fireAndForget } from '~/shared/lib/fireAndForget'

interface IEditOrCustomCreateActionsProps {
  closeModal: () => void
  carePlan: CarePlanV2ListResponse[number] | null
  patient: PatientUser
  me: RootState['me']
  handleModalAction: (carePlan: CarePlanCreateResponse | CarePlanRetrieveResponse) => void
  dispatchRemoveTasks: (tasks: Array<{ id: number }>) => Promise<void>
  saving: boolean
  setSaving: (arg0: boolean) => void
  tasks: CarePlanModalTask[]
  title: string
  notes: string
  internalOnly: boolean
}

const EditOrCustomCreateActions = (props: IEditOrCustomCreateActionsProps) => {
  const { tasks, saving, setSaving, title, notes, internalOnly } = props

  const saveIsDisabled = () => {
    // Save is disabled if:
    // 1) Currently saving
    // 2) Care plan title is empty
    // 3) Any task is incomplete. (Empty tasks will be thrown out on save).
    return saving || title === '' || anyIncompleteTask(tasks)
  }

  const newCarePlan = async () => {
    const {
      closeModal,
      handleModalAction,
      patient: { id },
    } = props
    const payload = {
      title: title,
      patient: id,
      notes: notes,
      visible_to_patient: !internalOnly,
      tasks: tasks.map(task => ({
        title: task.title,
        owner_group: task.owner_group,
        // Due date will be Converted as selected date with 19:00 EST time
        due_date: setDueDateToEOD(task.due_date),
        priority: task.priority,
      })),
    }
    const { data: createdCarePlan } = await carePlanApi.create({ data: payload })
    handleModalAction(createdCarePlan)
    closeModal()
  }

  /**
   * Complicated task to patch careplans since PATCHing
   * can't be used to remove tasks from a care plan, we
   * delete the ones not present in state
   */
  const patchCarePlan = async () => {
    const { closeModal, handleModalAction, carePlan } = props
    // patchCarePlan is only called when carePlan exists (via handleSave check)
    if (!carePlan) return
    // Find any omitted tasks, call the api to remove them, and remove them from redux
    const omittedTasks = (carePlan.tasks ?? []).filter(
      ({ id }) => !tasks.find(task => task.id === id),
    )
    if (omittedTasks.length) await props.dispatchRemoveTasks(omittedTasks)
    const payload = {
      title: title,
      notes: notes,
      visible_to_patient: !internalOnly,
      tasks: tasks
        // Remove any empty (invalid) tasks
        .filter(task => !!task.title && !!task.due_date && !!task.owner_group)
        .map(task => ({
          ...(task.id ? { id: task.id } : {}),
          title: task.title,
          owner_group: task.owner_group,
          // Due date will be Converted as selected date with 19:00 EST time
          due_date: setDueDateToEOD(task.due_date),
          priority: task.priority,
        })),
    }
    await carePlanApi.partialUpdate({ id: carePlan.id }, { data: payload })
    // Retrieve fresh data after update
    const { data: updatedCarePlan } = await carePlanApi.retrieve({ id: carePlan.id })
    handleModalAction(updatedCarePlan)
    closeModal()
  }

  const handleSave = () => {
    const { carePlan } = props
    const saveAction = carePlan ? patchCarePlan : newCarePlan
    setSaving(true)
    // The reset has to live inside the promise chain: fireAndForget never throws synchronously, so
    // a `catch` around it could not run, and a failed save would leave the button disabled behind
    // a spinner until the modal was closed and reopened. Only on failure -- the success path
    // closes the modal.
    fireAndForget('[CarePlans] save action', () =>
      saveAction().catch(error => {
        setSaving(false)
        throw error
      }),
    )
  }

  const getButtonText = () => {
    if (internalOnly) {
      return 'Save'
    }
    return 'Save & Send to Member'
  }

  const buttonContent = saving ? <Loader inline /> : getButtonText()

  return (
    <Box sx={{ m: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Button onClick={props.closeModal} disabled={saving} sx={{ px: '1em', mr: 1 }}>
        Cancel
      </Button>
      <Button
        disabled={saveIsDisabled()}
        sx={{ px: '1em', boxShadow: 'none' }}
        color="primary"
        variant="contained"
        onClick={handleSave}
      >
        {buttonContent}
      </Button>
    </Box>
  )
}

export default EditOrCustomCreateActions
